import { PermissionCode, type CsatAnalyticsQuery } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useCsatAnalytics, useCsatLookups } from '@/products/helpdesk/features/csat/use-csat';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';

function endOfDayIso(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

function startOfDayIso(date: string): string {
  return new Date(`${date}T00:00:00.000`).toISOString();
}

function formatRate(value: number): string {
  return `${Math.round(value * 1000) / 10} %`;
}

function defaultFromDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - 90);
  return date.toISOString().slice(0, 10);
}

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function CsatAnalyticsPage() {
  const me = useMe();
  const allowed = hasPermission(me.data, PermissionCode.CSAT_READ);
  const lookups = useCsatLookups(allowed);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(todayDate);
  const [departmentId, setDepartmentId] = useState('all');
  const [teamId, setTeamId] = useState('all');

  const query = useMemo<CsatAnalyticsQuery>(
    () => ({
      page: 1,
      pageSize: 25,
      from: fromDate ? startOfDayIso(fromDate) : undefined,
      to: toDate ? endOfDayIso(toDate) : undefined,
      departmentId: departmentId === 'all' ? undefined : departmentId,
      teamId: teamId === 'all' ? undefined : teamId,
    }),
    [fromDate, toDate, departmentId, teamId],
  );
  const analytics = useCsatAnalytics(query, allowed);
  const data = analytics.data;
  const teams = (lookups.data?.teams ?? []).filter(
    (team) => departmentId === 'all' || team.departmentId === departmentId,
  );
  const maxRatingCount = Math.max(1, ...(data?.byRating.map((item) => item.count) ?? [1]));

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Auswertung</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Zufriedenheit nach dem Schließen eines Tickets (1–5 Sterne), aufgeschlüsselt nach Agent,
          Kategorie und Zeitraum.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="csat-from">Von</Label>
          <Input
            id="csat-from"
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="csat-to">Bis</Label>
          <Input
            id="csat-to"
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label>Abteilung</Label>
          <Select
            value={departmentId}
            onValueChange={(value) => {
              setDepartmentId(value);
              setTeamId('all');
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              {(lookups.data?.departments ?? []).map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Team</Label>
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger>
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              {teams.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {analytics.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Kennzahlen werden geladen …
        </p>
      )}
      {analytics.isError && (
        <p className="text-sm text-muted-foreground">
          {analytics.error instanceof ApiRequestError
            ? analytics.error.message
            : 'Die Auswertung konnte nicht geladen werden.'}
        </p>
      )}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <Kpi title="Versendet" value={String(data.summary.sent)} />
            <Kpi title="Beantwortet" value={String(data.summary.responded)} />
            <Kpi title="Offen" value={String(data.summary.pending)} />
            <Kpi title="Rücklauf" value={formatRate(data.summary.responseRate)} />
            <Kpi
              title="Ø Sterne"
              value={
                data.summary.averageRating == null
                  ? '—'
                  : data.summary.averageRating.toLocaleString('de-DE')
              }
            />
            <Kpi title="Erinnerungen" value={String(data.summary.remindersSent)} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Sterne-Verteilung</CardTitle>
              <CardDescription>Nur beantwortete Umfragen im gewählten Zeitraum.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.byRating.map((item) => (
                <div key={item.rating} className="flex items-center gap-3 text-sm">
                  <span className="w-16 shrink-0">{item.rating} ★</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-amber-400"
                      style={{ width: `${(item.count / maxRatingCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-muted-foreground tabular-nums">
                    {item.count}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <NamedTable title="Nach Agent" rows={data.byAgent} />
            <NamedTable title="Nach Kategorie" rows={data.byCategory} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Zeitverlauf</CardTitle>
              <CardDescription>Antworten je Tag.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.byDay.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Noch keine Bewertungen in diesem Zeitraum.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tag</TableHead>
                      <TableHead className="text-right">Antworten</TableHead>
                      <TableHead className="text-right">Ø Sterne</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.byDay.map((row) => (
                      <TableRow key={row.date}>
                        <TableCell>{formatDate(row.date)}</TableCell>
                        <TableCell className="text-right tabular-nums">{row.responded}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.averageRating == null
                            ? '—'
                            : row.averageRating.toLocaleString('de-DE')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Aktuelle Kommentare</CardTitle>
              <CardDescription>Freitext aus beantworteten Umfragen.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.recentComments.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Kommentare im Zeitraum.</p>
              ) : (
                <ul className="divide-y">
                  {data.recentComments.map((item) => (
                    <li key={item.id} className="space-y-1 py-3">
                      <p className="text-sm font-medium">
                        <Link
                          className="hover:underline"
                          to={`/helpdesk/tickets/${item.ticketNumber}`}
                        >
                          #{item.ticketNumber} · {item.ticketSubject}
                        </Link>
                      </p>
                      <p className="text-sm">
                        {item.rating} ★{item.agentName ? ` · ${item.agentName}` : ''}
                        {item.categoryName ? ` · ${item.categoryName}` : ''}
                        <span className="text-muted-foreground">
                          {' '}
                          · {formatDateTime(item.respondedAt)}
                        </span>
                      </p>
                      <p className="text-sm text-muted-foreground">{item.comment}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </section>
  );
}

function Kpi({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl tabular-nums">{value}</CardTitle>
      </CardHeader>
    </Card>
  );
}

function NamedTable({
  title,
  rows,
}: {
  title: string;
  rows: { id: string | null; name: string; count: number; averageRating: number | null }[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Daten.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Antworten</TableHead>
                <TableHead className="text-right">Ø Sterne</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id ?? row.name}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.averageRating == null ? '—' : row.averageRating.toLocaleString('de-DE')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
